
import * as React from 'react'
import classnames from 'classnames'
import { ScheduleModel } from 'app/models/timesheet/scheduleModel'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import useTimesheetService from '@/modules/drivers/services/TimesheetService'
import { useDate } from '@/modules/drivers/stores/dateStore'
import { ScheduleCalendar } from '../schedule/ScheduleCalendar'
import ScheduleProvider from '../context/ScheduleContext'
import { useScheduleStore } from '@/modules/drivers/stores/scheduleStore'
import { LoaderIcon } from '@/app/features/icons/Loader'

interface ScheduleProps {
  companyId: string
  driverId: string
  year: number
  match?: any
  history?: any
  isCalculating: boolean
}

const Schedule: React.FC<ScheduleProps> = ({
  companyId,
  driverId,
  year,
  isCalculating,
}) => {
  const { t } = useLocalize()
  const { getSchedule } = useTimesheetService()
  const { setYear: setDateContextYear } = useDate()

  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [isInitialLoading, setIsInitialLoading] = React.useState<boolean>(true)

  const [detailsPanelVisible, setDetailsPanelVisible] =
    React.useState<boolean>(false)
  const [schedule, setSchedule] = React.useState<ScheduleModel | null>(null)

  const scheduleRefreshKey = useScheduleStore((s) => s.refreshKey)

  React.useEffect(() => {
    const fetchSchedule = async () => {
      if (driverId != null) {
        setIsInitialLoading(true)
        setIsLoading(true)
        try {
          const scheduleData = await getSchedule({
            companyId,
            driverId,
            year,
          })
          setSchedule(scheduleData)
        } catch (error) {
          console.error('Error fetching schedule:', error)
          setSchedule(null)
        } finally {
          setIsLoading(false)
          setIsInitialLoading(false)
        }
      }
    }

    fetchSchedule()
  }, [companyId, driverId, year, scheduleRefreshKey])

  React.useEffect(() => {
    if (year) {
      setDateContextYear(String(year))
    }
  }, [year])

  const displayContent = schedule && schedule.hasData

  return (
    <div
      className={classnames('tw-flex tw-h-full tw-w-full', {
        detailsPanel_open: detailsPanelVisible === true,
      })}
      style={{
        display: 'flex',
        height: '100%',
      }}
    >
      {isLoading && (
        <div className="tw-fixed tw-inset-0 tw-z-[9999] tw-grid tw-place-items-center tw-bg-black/40">
          <LoaderIcon className="tw-animate-spin" />
        </div>
      )}
      <div
        className="year-wrapper tw-relative tw-w-full"
        style={{
          position: 'relative',
        }}
      >
        {displayContent && (
          <ScheduleProvider>
            <div className="year-inner tw-w-full">
              {!isInitialLoading && (
                <ScheduleCalendar
                  driverId={Number(driverId)}
                  companyId={companyId}
                  schedule={schedule}
                />
              )}
            </div>
          </ScheduleProvider>
        )}
        {!displayContent && !isLoading && (
          <div className="no-data-wrapper tw-flex tw-items-center tw-justify-center">
            {t('schedule.no-data')}
          </div>
        )}
        {isCalculating && (
          <div className="tw-fixed tw-inset-0 tw-z-[10000] tw-grid tw-place-items-center tw-bg-black/40">
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded tw-bg-white tw-p-4">
              <LoaderIcon className="tw-animate-spin" />
              <p className="tw-text-lg tw-text-content-base">
                Trwa przeliczanie ewidencji
              </p>
              <span className="tw-text-sm tw-text-content-base">
                Nie zamykaj okna do zakończenia operacji
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Schedule
